import React, { useState, useMemo } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  Flag,
  Mail,
  Phone,
  Sparkles,
  TrendingUp,
  User,
  Users,
  X,
  XCircle,
  Zap,
} from 'lucide-react';

const STAGE_ORDER = ['Lead', 'Qualified', 'Discovery', 'Proposal', 'Verbal Commit', 'Closed-Won', 'Closed-Lost'];

const STAGE_CFG = {
  Lead:         { border: 'border-sky-400/25',     bg: 'bg-sky-500/8',    text: 'text-sky-300',     dot: 'bg-sky-400' },
  Qualified:    { border: 'border-cyan-400/25',    bg: 'bg-cyan-500/8',   text: 'text-cyan-300',    dot: 'bg-cyan-400' },
  Discovery:    { border: 'border-blue-400/25',    bg: 'bg-blue-500/8',   text: 'text-blue-300',    dot: 'bg-blue-400' },
  Proposal:     { border: 'border-violet-400/25',  bg: 'bg-violet-500/8', text: 'text-violet-300',  dot: 'bg-violet-400' },
  'Verbal Commit': { border: 'border-teal-400/25', bg: 'bg-teal-500/8', text: 'text-teal-300', dot: 'bg-teal-400' },
  'Closed-Won': { border: 'border-emerald-400/25', bg: 'bg-emerald-500/8',text: 'text-emerald-300', dot: 'bg-emerald-400' },
  'Closed-Lost':{ border: 'border-rose-400/25',    bg: 'bg-rose-500/8',   text: 'text-rose-300',    dot: 'bg-rose-400' },
};

const RISK_BAR = {
  Healthy:           'bg-emerald-400',
  Watch:             'bg-sky-400',
  'Needs attention': 'bg-amber-400',
  'At risk':         'bg-orange-400',
  Critical:          'bg-rose-400',
};

const SENTIMENT_CFG = {
  Warm:    { bg: 'bg-emerald-500/10', text: 'text-emerald-300', border: 'border-emerald-400/20' },
  Neutral: { bg: 'bg-slate-500/10',  text: 'text-slate-400',   border: 'border-slate-500/20' },
  Passive: { bg: 'bg-sky-500/10',    text: 'text-sky-300',     border: 'border-sky-400/20' },
  Ghosted: { bg: 'bg-amber-500/10',  text: 'text-amber-300',   border: 'border-amber-400/20' },
  Cold:    { bg: 'bg-rose-500/10',   text: 'text-rose-300',    border: 'border-rose-400/20' },
};

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function MetricTile({ label, value, sub, accent = 'text-white' }) {
  return (
    <div className="rounded-card border border-white/10 bg-white/[0.035] p-4">
      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl font-black ${accent}`}>{value}</div>
      {sub && <div className="mt-1 text-micro text-slate-600">{sub}</div>}
    </div>
  );
}

export default function AccountPanel({
  client,          // clientPortfolio entry
  graphCompany,    // organizationalGraph company entry (jobs, submissions, candidates, revenue, contacts)
  opportunities,   // filtered clientCrmOpportunities for this company
  touchpoints,     // filtered clientCrmTouchpoints for this company
  onLogEmail,
  onLogCall,
  onLogMeeting,
  onMoveStage,
  onSetOwner,
  onClose,
}) {
  const [tab, setTab] = useState('overview');
  const [ownerEditing, setOwnerEditing] = useState(false);
  const [ownerDraft, setOwnerDraft] = useState(client?.accountOwner || '');

  const companyName = client?.companyName || graphCompany?.name || 'Account';
  const riskLevel = client?.riskLevel || 'Healthy';
  const riskScore = client?.riskScore ?? 0;
  const barColor = RISK_BAR[riskLevel] || 'bg-slate-400';

  const initials = companyName.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
  const [logoFailed, setLogoFailed] = useState(false);

  // ── Revenue history: group by YYYY-MM from placed candidates ─────────────
  const revenueData = useMemo(() => {
    const map = {};
    (graphCompany?.candidates || []).forEach((c) => {
      if (!Number(c.placementFee || 0)) return;
      const month = (c.hiredAt || c.lastActivityAt || '').slice(0, 7);
      if (!month) return;
      map[month] = (map[month] || 0) + Number(c.placementFee);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).slice(-12);
  }, [graphCompany]);

  const totalRevenue = revenueData.reduce((s, [, v]) => s + v, 0);
  const accountRevenue = Math.max(totalRevenue, Number(client?.lifetimeRevenue || 0));
  const maxRevBar = Math.max(...revenueData.map(([, v]) => v), 1);

  // ── Sales forecast: open opps grouped by close quarter ───────────────────
  const forecastData = useMemo(() => {
    const map = {};
    opportunities.filter((o) => !['Closed-Won', 'Closed-Lost'].includes(o.stage)).forEach((o) => {
      const d = new Date(o.closeDate || o.expiryDate || o.updatedAtIso || o.createdAtIso || Date.now());
      const qLabel = `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
      if (!map[qLabel]) map[qLabel] = { label: qLabel, weighted: 0, count: 0, ms: d.getTime() };
      map[qLabel].weighted += Number(o.weightedValue || 0);
      map[qLabel].count += 1;
    });
    return Object.values(map).sort((a, b) => a.ms - b.ms).slice(0, 8);
  }, [opportunities]);

  const maxForecastBar = Math.max(...forecastData.map((f) => f.weighted), 1);

  // ── Win rate ─────────────────────────────────────────────────────────────
  const closedWon = opportunities.filter((o) => o.stage === 'Closed-Won').length;
  const closedLost = opportunities.filter((o) => o.stage === 'Closed-Lost').length;
  const closedTotal = closedWon + closedLost;
  const winRate = closedTotal > 0 ? Math.round((closedWon / closedTotal) * 100) : null;

  const openOpps = opportunities.filter((o) => !['Closed-Won', 'Closed-Lost'].includes(o.stage));
  const weightedPipeline = Math.max(openOpps.reduce((s, o) => s + Number(o.weightedValue || 0), 0), Number(client?.forecastValue || 0));

  const contacts = useMemo(() => {
    const seen = new Set();
    return (graphCompany?.contacts || client?.contacts || []).filter((c) => {
      const key = c.email || c.name;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [graphCompany, client]);

  const sortedTouchpoints = useMemo(() => (
    [...touchpoints].sort((a, b) => new Date(b.createdAtIso || 0) - new Date(a.createdAtIso || 0))
  ), [touchpoints]);

  const jobs = graphCompany?.jobs || [];
  const hiredCandidates = (graphCompany?.candidates || []).filter((c) => c.status === 'hired' || Number(c.placementFee || 0) > 0);

  const TABS = [
    { id: 'overview',      label: 'Overview' },
    { id: 'contacts',      label: `Contacts${contacts.length ? ` (${contacts.length})` : ''}` },
    { id: 'opportunities', label: `Pipeline${opportunities.length ? ` · ${opportunities.length}` : ''}` },
    { id: 'revenue',       label: `Revenue${totalRevenue > 0 ? ` · ${fmt(totalRevenue)}` : ''}` },
    { id: 'forecast',      label: 'Forecast' },
    { id: 'activity',      label: `Activity${sortedTouchpoints.length ? ` (${sortedTouchpoints.length})` : ''}` },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-slate-950">
      {/* ── Top bar ── */}
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-slate-950/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-meta font-black uppercase tracking-[0.14em] text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <X size={13} /> Close
          </button>
          <div className="hidden h-4 w-px bg-white/10 sm:block" />
          <div className="hidden text-xs text-slate-500 sm:block">Client CRM → {companyName}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onLogEmail && onLogEmail(client)}
            className="rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-300 transition hover:text-white flex items-center gap-1.5"
          >
            <Mail size={11} /> Log email
          </button>
          <button
            type="button"
            onClick={() => onLogCall && onLogCall(client)}
            className="rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-300 transition hover:text-white flex items-center gap-1.5"
          >
            <Phone size={11} /> Log call
          </button>
          <button
            type="button"
            onClick={() => onLogMeeting && onLogMeeting(client)}
            className="rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-cyan-300 transition hover:bg-cyan-500/20 flex items-center gap-1.5"
          >
            <Calendar size={11} /> Log meeting
          </button>
        </div>
      </div>

      {/* ── Hero ── */}
      <div className="shrink-0 border-b border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 px-8 py-5">
        <div className="mx-auto flex max-w-6xl items-start gap-6">
          {/* Logo / initials */}
          <div className="shrink-0">
            {client?.companyLogoUrl && !logoFailed ? (
              <img
                src={client.companyLogoUrl}
                alt=""
                className="h-14 w-14 rounded-ctl object-contain bg-white/10"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-ctl bg-slate-800 border border-white/10 text-base font-black text-white">
                {initials}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-black text-white">{companyName}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
              {client?.industry && <span>{client.industry}</span>}
              {jobs.length > 0 && (
                <>
                  {client?.industry && <span className="text-slate-600">·</span>}
                  <span className="flex items-center gap-1"><Briefcase size={11} /> {jobs.length} job order{jobs.length !== 1 ? 's' : ''}</span>
                </>
              )}
              {client?.linkedInUrl && (
                <a href={client.linkedInUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cyan-400 hover:underline">
                  <ExternalLink size={10} /> LinkedIn
                </a>
              )}
            </div>

            {/* Risk bar */}
            <div className="mt-3 flex items-center gap-3">
              <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500 shrink-0">Relationship health</div>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden max-w-[180px]">
                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${riskScore}%` }} />
              </div>
              <div className="text-micro font-black text-white">{riskScore}/100</div>
              <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.14em] ${barColor.replace('bg-', 'border-').replace('-400', '-400/30')} bg-white/5 text-white`}>
                {riskLevel}
              </span>
            </div>
          </div>

          {/* Account owner */}
          <div className="hidden shrink-0 flex-col gap-1 lg:flex">
            <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Account Owner</div>
            {ownerEditing ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={ownerDraft}
                  onChange={(e) => setOwnerDraft(e.target.value)}
                  onBlur={() => { setOwnerEditing(false); onSetOwner && onSetOwner(client?.id, ownerDraft); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setOwnerEditing(false); onSetOwner && onSetOwner(client?.id, ownerDraft); } if (e.key === 'Escape') setOwnerEditing(false); }}
                  className="w-36 rounded-ctl border border-cyan-400/40 bg-slate-900 px-3 py-1.5 text-xs text-white outline-none focus:border-cyan-400"
                  placeholder="Recruiter name…"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setOwnerDraft(client?.accountOwner || ''); setOwnerEditing(true); }}
                className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300 transition hover:border-cyan-400/30 hover:text-cyan-300"
              >
                <User size={12} />
                {client?.accountOwner || 'Assign owner'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="shrink-0 border-b border-white/10 px-8">
        <div className="mx-auto flex max-w-6xl gap-0 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`shrink-0 border-b-2 px-5 py-3.5 text-meta font-black uppercase tracking-[0.14em] transition ${
                tab === t.id ? 'border-cyan-400 text-cyan-300' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto max-w-6xl">

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && (
            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="space-y-5">
                {/* Key metrics */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <MetricTile label="Job orders" value={jobs.length} sub={`${(graphCompany?.openRoles || client?.openRoles || 0)} open`} />
                  <MetricTile label="Placements" value={Math.max(hiredCandidates.length, Number(client?.placementCount || 0))} sub={client?.submittalToHireRatio ? `Ratio ${client.submittalToHireRatio}` : undefined} accent="text-violet-300" />
                  <MetricTile label="Weighted pipeline" value={`${fmt(weightedPipeline)}`} sub={`${openOpps.length} open opps`} accent="text-cyan-300" />
                  <MetricTile label="Total revenue" value={fmt(accountRevenue)} sub={winRate != null ? `${winRate}% win rate` : 'No closed opps'} accent="text-emerald-300" />
                </div>

                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Account command center</div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    {[
                      ['Owner', client?.accountOwner || 'Unassigned'],
                      ['Tier', client?.accountTier || 'Prospect'],
                      ['Status', client?.status || 'Tracked'],
                      ['Forecast', fmt(weightedPipeline)],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-3">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
                        <div className="mt-1 truncate text-sm font-black text-white">{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-3">
                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Billing terms</div>
                      <div className="mt-1 text-xs leading-5 text-slate-300">{client?.billingTerms || 'Not captured yet'}</div>
                    </div>
                    <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-3">
                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Compliance needs</div>
                      <div className="mt-1 text-xs leading-5 text-slate-300">{client?.complianceNeeds || 'Not captured yet'}</div>
                    </div>
                  </div>
                </div>

                {/* Next best action */}
                {client?.nextBestAction && (
                  <div className="rounded-card border border-cyan-400/20 bg-cyan-500/5 px-5 py-4">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.2em] text-cyan-400">Recommended action</div>
                    <p className="text-sm leading-6 text-slate-200">{client.nextBestAction}</p>
                  </div>
                )}

                {/* Risk drivers */}
                {(client?.riskDrivers || []).length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Risk signals</div>
                    <div className="space-y-2">
                      {client.riskDrivers.map((d, i) => (
                        <div key={i} className={`flex items-start gap-3 rounded-ctl border px-3 py-2.5 ${
                          d.impact === 'high' ? 'border-rose-400/20 bg-rose-500/5' :
                          d.impact === 'medium' ? 'border-amber-400/20 bg-amber-500/5' :
                          'border-sky-400/15 bg-sky-500/5'
                        }`}>
                          <div className={`mt-0.5 shrink-0 text-xs font-black ${
                            d.impact === 'high' ? 'text-rose-400' :
                            d.impact === 'medium' ? 'text-amber-400' : 'text-sky-400'
                          }`}>
                            {d.impact === 'high' ? '●' : d.impact === 'medium' ? '◐' : '○'}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-200">{d.label}</div>
                            {d.detail && <div className="mt-0.5 text-micro text-slate-500">{d.detail}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Positive signals */}
                {(client?.healthSignals || []).length > 0 && (
                  <div className="rounded-card border border-emerald-400/15 bg-emerald-500/5 p-5">
                    <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-emerald-400">Positive signals</div>
                    <div className="flex flex-wrap gap-2">
                      {client.healthSignals.map((s, i) => (
                        <span key={i} className="flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-micro font-semibold text-emerald-300">
                          <CheckCircle2 size={10} /> {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Job orders quick view */}
                {jobs.length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Job orders</div>
                    <div className="space-y-2">
                      {jobs.slice(0, 6).map((job) => (
                        <div key={job.id} className="flex items-center gap-3 rounded-ctl border border-white/[0.06] bg-white/[0.03] px-4 py-2.5">
                          <Briefcase size={12} className="shrink-0 text-slate-500" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white truncate">{job.title}</div>
                            {job.location && <div className="text-micro text-slate-500">{job.location}</div>}
                          </div>
                          <span className={`shrink-0 rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${
                            String(job.status || '').toLowerCase() === 'active'
                              ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-300'
                              : 'border-slate-700 bg-slate-800/60 text-slate-500'
                          }`}>
                            {job.status || 'Active'}
                          </span>
                        </div>
                      ))}
                      {jobs.length > 6 && (
                        <div className="text-center text-xs text-slate-600 py-1">+{jobs.length - 6} more job orders</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right sidebar */}
              <div className="space-y-5">
                {/* Relationship stats */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Relationship stats</div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Last touch</span>
                      <span className="font-semibold text-white">
                        {client?.daysSinceTouch >= 999 ? 'Never' : client?.daysSinceTouch != null ? `${client.daysSinceTouch}d ago` : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Touchpoints</span>
                      <span className="font-semibold text-white">{touchpoints.length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Submittal ratio</span>
                      <span className="font-semibold text-white">{client?.submittalToHireRatio || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Win rate</span>
                      <span className={`font-black ${winRate != null ? 'text-emerald-300' : 'text-slate-600'}`}>
                        {winRate != null ? `${winRate}%` : 'No data'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Sales queue score</span>
                      <span className="font-black text-cyan-300">{client?.salesQueueScore ?? '—'}</span>
                    </div>
                  </div>
                </div>

                {/* Department mix */}
                {graphCompany?.departmentMix && Object.keys(graphCompany.departmentMix).length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Department demand</div>
                    <div className="space-y-2">
                      {Object.entries(graphCompany.departmentMix).map(([dept, count]) => (
                        <div key={dept} className="flex items-center gap-3">
                          <div className="flex-1 text-xs text-slate-300">{dept}</div>
                          <div className="text-xs font-black text-white">{count}</div>
                          <div className="w-16 h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-cyan-400"
                              style={{ width: `${Math.min((count / Math.max(...Object.values(graphCompany.departmentMix))) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Revenue snapshot */}
                <div className="rounded-card border border-violet-400/15 bg-violet-500/5 p-5">
                  <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-violet-400">Revenue snapshot</div>
                  <div className="text-3xl font-black text-white">{fmt(totalRevenue)}</div>
                  <div className="mt-1 text-micro text-slate-500">{hiredCandidates.length} placement{hiredCandidates.length !== 1 ? 's' : ''}</div>
                  {revenueData.length > 0 && (
                    <div className="mt-4 flex items-end gap-1" style={{ height: 40 }}>
                      {revenueData.map(([month, val]) => (
                        <div
                          key={month}
                          className="flex-1 rounded-t bg-violet-400 opacity-70 hover:opacity-100 transition"
                          style={{ height: `${Math.max((val / maxRevBar) * 40, 3)}px` }}
                          title={`${month}: ${fmt(val)}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── CONTACTS ── */}
          {tab === 'contacts' && (
            <div className="max-w-3xl space-y-3">
              {contacts.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Users size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No contacts mapped to this account.</div>
                  <div className="mt-1 text-xs text-slate-600">Open a LinkedIn profile from this company and use the Vision extension to capture the contact.</div>
                  <a
                    href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(companyName)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-cyan-300 hover:bg-cyan-500/20 transition"
                  >
                    <ExternalLink size={12} /> Find contacts on LinkedIn
                  </a>
                </div>
              ) : contacts.map((c, i) => {
                const sentCfg = SENTIMENT_CFG[c.sentiment] || SENTIMENT_CFG.Neutral;
                return (
                  <div key={c.id || i} className="rounded-card border border-white/[0.08] bg-slate-900/50 p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        {c.photoUrl ? (
                          <img src={c.photoUrl} alt="" className="h-9 w-9 shrink-0 rounded-full border border-white/10 object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-800 text-xs font-black text-white border border-white/10">
                            {(c.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-bold text-white">{c.name || '—'}</div>
                          <div className="mt-0.5 text-xs text-slate-400">{c.role || c.title || 'Contact'}</div>
                          {c.contactRole === 'hr_hiring' && <div className="mt-1 text-micro font-black uppercase tracking-[0.12em] text-emerald-400">Hiring contact</div>}
                          {c.vibe && c.vibe !== 'Balanced Vibe' && (
                            <div className="mt-1 text-micro text-slate-500 italic">{c.vibe}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${sentCfg.border} ${sentCfg.bg} ${sentCfg.text}`}>
                          {c.sentiment || 'Neutral'}
                        </span>
                        {c.source && (
                          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-micro text-slate-500">{c.source.replace(/_/g, ' ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {c.email && (
                        <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-cyan-400 hover:underline">
                          <Mail size={11} /> {c.email}
                        </a>
                      )}
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-xs text-emerald-400 hover:underline">
                          <Phone size={11} /> {c.phone}
                        </a>
                      )}
                      {(c.linkedInUrl || c.linkedinUrl) && (
                        <a href={c.linkedInUrl || c.linkedinUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-sky-400 hover:underline">
                          <ExternalLink size={11} /> LinkedIn
                        </a>
                      )}
                    </div>
                    {(c.generatedRevenue > 0 || c.submittedCandidates > 0) && (
                      <div className="mt-3 flex gap-4 text-xs text-slate-500">
                        {c.submittedCandidates > 0 && <span>{c.submittedCandidates} submissions</span>}
                        {c.generatedRevenue > 0 && <span className="text-emerald-400">{fmt(c.generatedRevenue)} revenue</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── OPPORTUNITIES ── */}
          {tab === 'opportunities' && (
            <div className="max-w-3xl space-y-5">
              {/* Pipeline funnel summary */}
              {opportunities.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {STAGE_ORDER.map((stage) => {
                    const count = opportunities.filter((o) => o.stage === stage).length;
                    const value = opportunities.filter((o) => o.stage === stage).reduce((s, o) => s + Number(o.weightedValue || 0), 0);
                    const cfg = STAGE_CFG[stage] || STAGE_CFG.Lead;
                    return (
                      <div key={stage} className={`rounded-card border p-3 text-center ${count > 0 ? `${cfg.border} ${cfg.bg}` : 'border-white/5 bg-white/[0.02]'}`}>
                        <div className={`text-xl font-black ${count > 0 ? cfg.text : 'text-slate-700'}`}>{count}</div>
                        <div className="mt-0.5 text-micro font-black uppercase tracking-[0.1em] text-slate-500">{stage.replace('-', '\u2011')}</div>
                        {value > 0 && <div className={`mt-1 text-micro font-semibold ${cfg.text}`}>{fmt(value)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}

              {opportunities.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Banknote size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No opportunities for this account yet.</div>
                </div>
              ) : opportunities.map((opp) => {
                const cfg = STAGE_CFG[opp.stage] || STAGE_CFG.Lead;
                return (
                  <div key={opp.id} className={`rounded-card border p-5 ${cfg.border} ${cfg.bg}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <div className="text-sm font-bold text-white">{opp.title || 'Opportunity'}</div>
                        {opp.contactName && <div className="mt-0.5 text-xs text-slate-400">{opp.contactName}{opp.contactEmail ? ` · ${opp.contactEmail}` : ''}</div>}
                      </div>
                      <span className={`shrink-0 flex items-center gap-1.5 rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.12em] ${cfg.border} ${cfg.bg} ${cfg.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} /> {opp.stage}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div>
                        <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Fee</div>
                        <div className="mt-1 text-sm font-bold text-white">{opp.fee ? fmt(opp.fee) : '—'}</div>
                      </div>
                      <div>
                        <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Probability</div>
                        <div className="mt-1 text-sm font-bold text-white">{opp.probability ? `${Math.round(opp.probability * 100)}%` : '—'}</div>
                      </div>
                      <div>
                        <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Weighted value</div>
                        <div className="mt-1 text-sm font-bold text-cyan-300">{opp.weightedValue ? fmt(opp.weightedValue) : '—'}</div>
                      </div>
                    </div>

                    {opp.ownerName && (
                      <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
                        <User size={11} /> Owner: <span className="text-slate-300">{opp.ownerName}</span>
                      </div>
                    )}

                    {/* Stage move buttons */}
                    {onMoveStage && !['Closed-Won', 'Closed-Lost'].includes(opp.stage) && (
                      <div className="flex flex-wrap gap-2">
                        {STAGE_ORDER.filter((s) => s !== opp.stage).map((s) => {
                          const tc = STAGE_CFG[s];
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => onMoveStage(opp.id, s)}
                              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] transition hover:opacity-80 ${tc.border} ${tc.bg} ${tc.text}`}
                            >
                              <ArrowRight size={9} /> {s}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-3 text-micro text-slate-600">
                      Created {fmtDate(opp.createdAtIso)}{opp.closeDate ? ` · Close target ${fmtDate(opp.closeDate)}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── REVENUE ── */}
          {tab === 'revenue' && (
            <div className="max-w-3xl space-y-6">
              {/* Summary row */}
              <div className="grid grid-cols-3 gap-4">
                <MetricTile label="Total revenue" value={fmt(totalRevenue)} accent="text-emerald-300" />
                <MetricTile label="Placements" value={hiredCandidates.length} />
                <MetricTile
                  label="Avg fee"
                  value={hiredCandidates.length > 0 ? fmt(Math.round(totalRevenue / hiredCandidates.length)) : '—'}
                  accent="text-violet-300"
                />
              </div>

              {revenueData.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <TrendingUp size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No placement revenue recorded for this account.</div>
                  <div className="mt-1 text-xs text-slate-600">Placement fees are recorded when a candidate is marked as Hired with a fee value.</div>
                </div>
              ) : (
                <>
                  {/* Bar chart */}
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Revenue by month</div>
                    <div className="flex items-end gap-2" style={{ height: 120 }}>
                      {revenueData.map(([month, val]) => (
                        <div key={month} className="flex flex-1 flex-col items-center gap-1">
                          <div className="text-micro font-bold text-violet-300">{fmt(val)}</div>
                          <div
                            className="w-full rounded-t-lg bg-violet-400 transition-all"
                            style={{ height: `${Math.max((val / maxRevBar) * 90, 4)}px` }}
                            title={`${month}: ${fmt(val)}`}
                          />
                          <div className="text-micro text-slate-600 rotate-45 origin-left mt-1 w-8 overflow-hidden">
                            {month.slice(5)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Placed candidates table */}
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Placement history</div>
                    <div className="space-y-2">
                      {hiredCandidates.map((c) => (
                        <div key={c.id} className="flex items-center gap-4 rounded-ctl border border-white/[0.06] bg-white/[0.025] px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white truncate">{c.name}</div>
                            <div className="text-xs text-slate-500">{c.matchedJobTitle || c.role || '—'}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-black text-emerald-300">{fmt(c.placementFee)}</div>
                            <div className="text-micro text-slate-600">{fmtDate(c.hiredAt || c.lastActivityAt)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── FORECAST ── */}
          {tab === 'forecast' && (
            <div className="max-w-3xl space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <MetricTile label="Open pipeline" value={fmt(weightedPipeline)} sub={`${openOpps.length} active opps`} accent="text-cyan-300" />
                <MetricTile label="Closed-Won" value={fmt(opportunities.filter((o) => o.stage === 'Closed-Won').reduce((s, o) => s + Number(o.fee || 0), 0))} sub={`${closedWon} won`} accent="text-emerald-300" />
                <MetricTile label="At-risk" value={fmt(opportunities.filter((o) => o.stage === 'Closed-Lost').reduce((s, o) => s + Number(o.fee || 0), 0))} sub={`${closedLost} lost`} accent="text-rose-300" />
              </div>

              {forecastData.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Sparkles size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No forecast data yet.</div>
                  <div className="mt-1 text-xs text-slate-600">Set close dates on open opportunities to build the forecast.</div>
                </div>
              ) : (
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Weighted pipeline by quarter</div>
                  <div className="space-y-3">
                    {forecastData.map((q) => (
                      <div key={q.label} className="flex items-center gap-4">
                        <div className="w-16 shrink-0 text-xs font-bold text-slate-300">{q.label}</div>
                        <div className="flex-1 h-6 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all"
                            style={{ width: `${Math.max((q.weighted / maxForecastBar) * 100, 2)}%` }}
                          />
                        </div>
                        <div className="w-20 shrink-0 text-right">
                          <div className="text-xs font-black text-cyan-300">{fmt(q.weighted)}</div>
                          <div className="text-micro text-slate-600">{q.count} opp{q.count !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All open opps with close dates */}
              {openOpps.length > 0 && (
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Open opportunities</div>
                  <div className="space-y-2">
                    {openOpps.map((opp) => {
                      const cfg = STAGE_CFG[opp.stage] || STAGE_CFG.Lead;
                      return (
                        <div key={opp.id} className="flex items-center gap-3 rounded-ctl border border-white/[0.06] bg-white/[0.025] px-4 py-3">
                          <span className={`h-2 w-2 shrink-0 rounded-full ${cfg.dot}`} />
                          <div className="flex-1 min-w-0 text-sm font-semibold text-slate-200 truncate">{opp.title}</div>
                          <div className="shrink-0 text-right">
                            <div className="text-xs font-black text-cyan-300">{fmt(opp.weightedValue)}</div>
                            <div className="text-micro text-slate-600">{opp.stage}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ACTIVITY ── */}
          {tab === 'activity' && (
            <div className="max-w-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-500">{sortedTouchpoints.length} activity events</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => onLogEmail && onLogEmail(client)} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black text-slate-300 hover:text-white transition"><Mail size={10} /> Email</button>
                  <button type="button" onClick={() => onLogCall && onLogCall(client)} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black text-slate-300 hover:text-white transition"><Phone size={10} /> Call</button>
                  <button type="button" onClick={() => onLogMeeting && onLogMeeting(client)} className="flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 text-micro font-black text-cyan-300 hover:bg-cyan-500/20 transition"><Calendar size={10} /> Meeting</button>
                </div>
              </div>

              {sortedTouchpoints.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Calendar size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No activity logged yet for this account.</div>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute bottom-4 left-[19px] top-4 w-px bg-white/10" />
                  {sortedTouchpoints.map((tp, i) => {
                    const isEmail = /email|outreach|sequence/i.test(tp.type || '');
                    const isCall = /call|phone/i.test(tp.type || '');
                    const isMeeting = /meeting|visit|event/i.test(tp.type || '');
                    return (
                      <div key={tp.id || i} className="relative flex gap-4 py-2.5">
                        <div className={`relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                          isEmail ? 'border-cyan-400/30 bg-cyan-500/10' :
                          isCall ? 'border-emerald-400/30 bg-emerald-500/10' :
                          isMeeting ? 'border-violet-400/30 bg-violet-500/10' :
                          'border-white/20 bg-slate-800'
                        }`}>
                          {isEmail ? <Mail size={9} className="text-cyan-400" /> :
                           isCall ? <Phone size={9} className="text-emerald-400" /> :
                           isMeeting ? <Calendar size={9} className="text-violet-400" /> :
                           <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />}
                        </div>
                        <div className="flex-1 rounded-card border border-white/[0.07] bg-slate-900/50 px-4 py-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-slate-200 leading-5">{tp.summary}</p>
                            <div className="shrink-0 text-micro text-slate-600">{fmtDate(tp.createdAtIso)}</div>
                          </div>
                          {tp.type && (
                            <div className="mt-1.5 inline-block rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro font-semibold text-slate-500">
                              {String(tp.type).replace(/_/g, ' ')}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
